import { useState, useEffect } from "react";
import { Offcanvas, CloseButton, Form, Table } from "react-bootstrap";
import { Button as ButtonAntd } from "antd";
import { toast } from "react-toastify";

export default function BulkRoutesDrawer({ show, onClose, bulkImportRoutes }) {
  const [visible, setVisible] = useState(false);
  const [jsonInput, setJsonInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState([]);
  const [validRoutes, setValidRoutes] = useState([]);

  useEffect(() => {
    if (show) setVisible(true);
  }, [show]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
    setJsonInput("");
    setErrors([]);
    setValidRoutes([]);
  };

  // apply defaults and convert scopes object
  const preprocessRoutes = (routes) => {
    return routes.map((route) => {
      const processed = {
        path: route.path || "",
        priority: route.priority ?? 100,
        active: route.active ?? true,
      };

      if (Array.isArray(route.scopeRules)) {
        processed.scopeRules = route.scopeRules;
      } else if (route.scopes && typeof route.scopes === "object") {
        // convert scopes object to scopeRules array
        const scopeRules = [];
        Object.entries(route.scopes).forEach(([scope, methods]) => {
          methods.forEach((method) => {
            scopeRules.push({ method, scope, block: false });
          });
        });
        processed.scopeRules = scopeRules;
      } else {
        processed.scopeRules = [];
      }

      return processed;
    });
  };

  // Validate routes
  const validateRoutes = (routes) => {
    const errs = [];
    const valid = [];

    routes.forEach((route, idx) => {
      const routeErrors = [];

      if (!route.path || typeof route.path !== "string")
        routeErrors.push("path must be string");
      if (typeof route.active !== "boolean")
        routeErrors.push("active must be boolean");
      if (typeof route.priority !== "number")
        routeErrors.push("priority must be number");
      if (!Array.isArray(route.scopeRules))
        routeErrors.push("scopeRules must be array");
      else {
        route.scopeRules.forEach((rule, ridx) => {
          if (typeof rule.method !== "string")
            routeErrors.push(`scopeRule ${ridx + 1}: method must be string`);
          if (typeof rule.scope !== "string")
            routeErrors.push(`scopeRule ${ridx + 1}: scope must be string`);
          if (typeof rule.block !== "boolean")
            routeErrors.push(`scopeRule ${ridx + 1}: block must be boolean`);
        });
      }

      if (routeErrors.length > 0) {
        errs.push(`Route ${idx + 1}: ${routeErrors.join(", ")}`);
      } else {
        valid.push(route);
      }
    });

    return { errors: errs, valid };
  };

  const handleValidate = () => {
    let parsed;
    try {
      parsed = JSON.parse(jsonInput);
    } catch (err) {
      setErrors([`Invalid JSON: ${err.message}`]);
      setValidRoutes([]);
      toast.error("Invalid JSON format");
      return;
    }

    if (!Array.isArray(parsed)) {
      setErrors(["Top-level JSON must be an array of routes"]);
      setValidRoutes([]);
      toast.error("JSON must be an array of routes");
      return;
    }

    const preprocessed = preprocessRoutes(parsed);
    const { errors, valid } = validateRoutes(preprocessed);

    setErrors(errors);
    setValidRoutes(valid);

    if (errors.length === 0) {
      toast.success(
        `Validation successful! ${valid.length} route(s) ready to submit.`
      );
    } else {
      toast.error(`Validation completed with ${errors.length} error(s)`);
    }
  };

  const handleSubmit = async () => {
    if (validRoutes.length === 0) {
      toast.error("No valid routes to submit. Please validate JSON first.");
      return;
      }
      

    setIsSubmitting(true);
    try {
      await bulkImportRoutes(validRoutes);
      handleClose();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Offcanvas
      show={visible}
      onHide={handleClose}
      placement="end"
      style={{ width: "30rem" }}
      className="border-0 d-flex flex-column"
    >
      <div className="offcanvas-header px-4">
        <h5 className="text-base fw-bold">Add Bulk Routes</h5>
        <CloseButton
          className="btn btn-circle btn-sm transition-base p-0 text-xxs"
          onClick={handleClose}
        />
      </div>

      <div className="flex-grow-1 overflow-auto px-4">
        <Form.Group className="mb-3">
          <Form.Label>Paste routes JSON</Form.Label>
          <Form.Control
            as="textarea"
            rows={18}
            value={jsonInput}
            onChange={(e) => setJsonInput(e.target.value)}
            placeholder="Paste your JSON array of routes here..."
            className="font-monospace"
            isInvalid={errors.length > 0}
          />
          <Form.Control.Feedback type="invalid">
            {errors.length > 0 && (
              <ul className="mb-0 ps-3">
                {errors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            )}
          </Form.Control.Feedback>
        </Form.Group>

        {validRoutes.length > 0 && (
          <>
            <p className="mb-2 text-success text-xs">
              {validRoutes.length} valid route(s) detected
            </p>
            <div style={{ maxHeight: 200, overflowY: "auto" }}>
              <Table bordered size="sm" className="text-xs font-monospace p-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Path</th>
                    <th>Priority</th>
                    <th>Active</th>
                  </tr>
                </thead>
                <tbody>
                  {validRoutes.map((route, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>{route.path}</td>
                      <td>{route.priority}</td>
                      <td>{route.active ? "Yes" : "No"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </div>

      <div className="border-top p-3 bg-light text-end">
        <ButtonAntd
          type="default"
          size="sm"
          className="ms-2 bg-secondary text-white"
          onClick={handleClose}
        >
          Close
        </ButtonAntd>

        <ButtonAntd
          type="default"
          size="sm"
          className="ms-2 bg-info text-white"
          onClick={handleValidate}
        >
          Validate
        </ButtonAntd>

        <ButtonAntd
          type="primary"
          size="sm"
          className="ms-2 bg-success text-white"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={validRoutes.length === 0}
        >
          Submit
        </ButtonAntd>
      </div>
    </Offcanvas>
  );
}
